import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import {
  applySnapshot,
  cast,
  clone,
  destroy,
  detach,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import {
  getConstantName,
  getReading,
  isArchiveMeter,
  isConfiguredMeter,
  killNulls,
} from 'utils';
import {
  AutomationMetersFilterOptions,
  IConstant,
  INewReadings,
  IObject,
} from 'types';
import { Meters } from 'http-requests';
import { INewAreaMeterReadings } from 'http-requests/meters/meters';

import { FileDTO, IUploadFile, RequestStateModel } from './general-models';

export interface ISeasonsMapping extends Instance<typeof SeasonsMappingModel> {}
export interface ISeasonChange extends Instance<typeof SeasonChangeModel> {}
export interface IAdapter extends Instance<typeof AdapterModel> {}
export interface IExpances extends Instance<typeof ExpancesModel> {}
export interface IEquipInfo extends Instance<typeof EquipInfoModel> {}
export interface IConnectionSchema extends Instance<
  typeof ConnectionSchemaModel
> {}
export interface IHeatSystem extends Instance<typeof HeatSystemModel> {}
export interface ICheck extends Instance<typeof CheckModel> {}
export interface IAreaMeterAllReadings extends Instance<
  typeof AreaMeterAllReadingsModel
> {}
export interface IAreaMeter extends Instance<typeof AreaMeterModel> {}
export interface IHouseMeter extends Instance<typeof HouseMeterModel> {}

const RequestsModel = types.model({
  areaMeters: types.optional(RequestStateModel, {}),
  areaMetersAllReadings: types.optional(RequestStateModel, {}),
  newReadings: types.optional(RequestStateModel, {}),
  heatHouseMeters: types.optional(RequestStateModel, {}),
  meterData: types.optional(RequestStateModel, {}),
  createMeter: types.optional(RequestStateModel, {}),
  replaceMeter: types.optional(RequestStateModel, {}),
  patchMeter: types.optional(RequestStateModel, {}),
  deleteMeter: types.optional(RequestStateModel, {}),
  saveMeterFiles: types.optional(RequestStateModel, {}),
  sendSettings: types.optional(RequestStateModel, {}),
});

const DeveloperModel = types.model({
  str_name: types.optional(types.string, ''),
  _id: types.optional(types.string, ''),
});

const AdapterModel = types
  .model({
    id: types.optional(types.maybeNull(types.string), null),
    _type: types.optional(types.array(types.string), []),
    model: types.optional(types.maybeNull(types.string), null),
    phone_number: types.optional(types.maybeNull(types.string), null),
    sim_number: types.optional(types.maybeNull(types.string), null),
    app_number: types.optional(types.maybeNull(types.string), null),
    account_number: types.optional(types.maybeNull(types.string), null),
    adapter_id: types.optional(types.maybeNull(types.string), null),
    tcp_port: types.optional(types.maybeNull(types.number), null),
    tuned: types.optional(types.boolean, false),
    gprs_operator: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    get isSaveDisabled(): boolean {
      return (
        !self.phone_number ||
        self.phone_number.length !== 11 ||
        (self._type[0] === 'GprsAdapter' &&
          (!self.model || !self.gprs_operator || !self.tcp_port))
      );
    },
  }));

export const ConnectionSchemaModel = types
  .model({
    available_hot_water_supply: types.optional(
      types.maybeNull(types.string),
      null
    ),
    pipe_count: types.optional(types.maybeNull(types.string), null),
    autonomous: types.optional(types.maybeNull(types.string), null),
    circulation: types.optional(types.maybeNull(types.string), null),
  })
  .actions((self) => ({
    setConnectionSchema(newValue: IConnectionSchema) {
      applySnapshot(self, newValue);
    },
    setField(
      key:
        | 'available_hot_water_supply'
        | 'circulation'
        | 'autonomous'
        | 'pipe_count',
      value: string
    ) {
      self[key] = value;
    },
  }));

const SeasonModel = types.model({
  sensors: types.optional(types.frozen<{ [key: string]: any }>(), {}),
  formulas: types.optional(types.frozen<{ [key: string]: any }>(), {}),
});

const SeasonsMappingModel = types.model({
  winter: types.optional(SeasonModel, {}),
  summer: types.optional(SeasonModel, {}),
});

const SeasonChangeModel = types.model({
  season: types.optional(types.string, ''),
  date: types.optional(types.string, ''),
});

const EquipInfoModel = types
  .model({
    title: types.optional(types.string, ''),
    var_name: types.optional(types.string, ''),
    flowmeter: types.optional(types.string, ''),
    g_min: types.optional(types.string, ''),
    g_max: types.optional(types.string, ''),
    T_conversion: types.optional(types.string, ''),
    V_conversion: types.optional(types.string, ''),
  })
  .views((self) => ({
    get isSaveDisabled(): boolean {
      return (
        !self.title ||
        !self.var_name ||
        !self.flowmeter ||
        !self.g_min ||
        !self.g_max ||
        !self.T_conversion ||
        !self.V_conversion
      );
    },
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

export const ExpancesModel = types.model({
  _id: types.optional(types.string, ''),
  central_heating: types.optional(types.array(types.number), []),
  central_vent: types.optional(types.maybeNull(types.number), null),
  tech_heating: types.optional(types.maybeNull(types.number), null),
  tech_hot_water_supply: types.optional(types.maybeNull(types.number), null),
  avg_tech_hot_water_supply: types.optional(
    types.maybeNull(types.number),
    null
  ),
  hot_water_supply: types.optional(types.array(types.number), []),
});

const HeatSystemModel = types
  .model({
    name: types.optional(types.string, ''),
    mappings: types.optional(SeasonsMappingModel, {}),
  })
  .actions((self) => ({
    setName(value: string) {
      self.name = value;
    },
    setMapping(newMap: ISeasonsMapping) {
      self.mappings = newMap;
    },
  }));

const HouseModel = types.model({
  address: types.optional(types.string, ''),
  fias_addrobjs: types.optional(types.array(types.string), []),
  developer: types.optional(types.maybeNull(DeveloperModel), null),
  id: types.optional(types.string, ''),
});

export const AreaModel = types.model({
  gis_uid: types.optional(types.maybeNull(types.string), null),
  house: types.optional(HouseModel, {}),
  id: types.optional(types.string, ''),
  number: types.optional(types.number, 0),
  order: types.optional(types.number, 0),
  str_number: types.optional(types.string, ''),
  is_shared: types.optional(types.maybeNull(types.boolean), null),
  str_number_full: types.optional(types.string, ''),
  _type: types.optional(types.array(types.string), []),
});

const CheckModel = types
  .model('CheckModel', {
    attached_check_act: types.optional(types.maybeNull(FileDTO), null),
    attached_seal_act: types.optional(types.maybeNull(FileDTO), null),
    check_date: types.optional(types.maybeNull(types.string), null),
    expiration_date_check: types.optional(types.number, 0),
    next_check_date: types.optional(types.string, ''),
    seal_number: types.optional(types.maybeNull(types.string), null),
    working_start_date: types.optional(types.string, ''),
  })
  .actions((self) => ({
    updateField<K extends keyof typeof self>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
  }));

const ReadingsModel = types.model({
  author: types.optional(types.maybeNull(types.string), null),
  comment: types.optional(types.maybeNull(types.string), null),
  created_at: types.optional(types.string, ''),
  created_by: types.optional(types.string, ''),
  deltas: types.optional(types.array(types.number), []),
  id: types.optional(types.string, ''),
  period: types.optional(types.string, ''),
  points: types.optional(types.array(types.number), []),
  recalculation: types.optional(types.maybeNull(types.number), null),
  status: types.optional(types.string, ''),
  values: types.optional(types.array(types.number), []),
});

const ReadingsModelLight = types.model({
  created_at: types.optional(types.string, ''),
  deltas: types.optional(types.array(types.number), []),
  period: types.optional(types.string, ''),
  points: types.optional(types.array(types.number), []),
  values: types.optional(types.array(types.number), []),
});

const AreaMeterAllReadingsModel = types
  .model({
    average_deltas: types.optional(types.array(types.number), []),
    current_values: types.optional(
      types.maybeNull(types.array(types.number)),
      null
    ),
    digit_capacity: types.optional(types.number, 0),
    first_values: types.optional(types.array(types.number), []),
    id: types.optional(types.string, ''),
    installation_date: types.optional(types.string, ''),
    is_automatic: types.optional(types.boolean, false),
    last_check_date: types.optional(types.string, ''),
    last_readings: types.optional(
      types.maybeNull(types.array(types.number)),
      []
    ),
    last_readings_date: types.optional(types.maybeNull(types.string), ''),
    location: types.optional(types.string, ''),
    next_check_date: types.optional(types.string, ''),
    previous_date: types.optional(types.maybeNull(types.string), ''),
    previous_values: types.optional(types.array(types.number), []),
    readings: types.optional(types.array(ReadingsModelLight), []),
    readonly: types.optional(types.boolean, false),
    reverse: types.optional(types.boolean, false),
    serial_number: types.optional(types.string, ''),
    type: types.optional(types.string, ''),
    working_finish_date: types.optional(types.maybeNull(types.string), null),
    working_start_date: types.optional(types.string, ''),
    mounting: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    /* из вне не используем этот view, он используется в следующих вьюшках currentReadings и lastReadings */
    initialReadingsGetter(
      field: 'current_values' | 'previous_values'
    ): string[] {
      const data = self[field];

      return data?.length
        ? data.map((value) => getReading(value))
        : self.type === 'ElectricThreeRateAreaMeter'
          ? ['0', '0', '0']
          : self.type === 'ElectricTwoRateAreaMeter'
            ? ['0', '0']
            : ['0'];
    },
    get currentReadings(): string[] {
      return this.initialReadingsGetter('current_values');
    },
    get lastReadings(): string[] {
      return this.initialReadingsGetter('previous_values');
    },
  }));

const BaseMeterModel = types
  .model('BaseMeterModel', {
    attached_passport: types.maybeNull(FileDTO),
    attached_seal_act: types.maybeNull(FileDTO),
    average_deltas: types.optional(types.array(types.number), []),
    brand_name: types.optional(types.maybeNull(types.string), null),
    check_history: types.optional(types.array(CheckModel), []),
    closed_by: types.optional(types.maybeNull(types.string), null),
    created_by: types.optional(types.maybeNull(types.string), ''),
    description: types.optional(types.maybeNull(types.string), null),
    digit_capacity: types.optional(types.number, 0),
    expiration_date_check: types.optional(types.number, 0),
    first_check_date: types.optional(types.maybeNull(types.string), null),
    gis_uid: types.optional(types.maybeNull(types.string), ''), //???
    id: types.optional(types.string, ''),
    initial_values: types.optional(types.array(types.number), []),
    installation_date: types.optional(types.string, moment().toISOString(true)),
    is_deleted: types.optional(types.maybeNull(types.boolean), false),
    last_check_date: types.optional(types.maybeNull(types.string), null),
    loss_ratio: types.optional(types.maybeNull(types.number), 0), //это коэффициент потерь электроэнергии
    model_name: types.optional(types.maybeNull(types.string), null),
    next_check_date: types.optional(types.string, ''),
    order: types.optional(types.number, 0),
    ratio: types.optional(types.number, 0),
    readings: types.optional(types.array(ReadingsModel), []),
    serial_number: types.optional(types.string, ''),
    unit_of_measurement_okei: types.optional(
      types.maybeNull(types.string),
      null
    ),
    working_finish_date: types.optional(types.maybeNull(types.string), null),
    working_start_date: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get lastCheckHistory(): ICheck {
      return self.check_history[self.check_history.length - 1];
    },
    get currentReadings() {
      return self.readings[self.readings.length - 1];
    },
    get lastReadings() {
      return self.readings[self.readings.length - 2];
    },
    //Акт поверки
    get attachedCheckAct() {
      if (!self.check_history.length) {
        return null;
      }

      return this.lastCheckHistory.attached_check_act;
    },
    //Акт ввода в эксплуатацию
    get attachedSealAct() {
      if (!self.check_history.length) {
        return null;
      }

      return this.lastCheckHistory.attached_seal_act;
    },
    get isCheckHistoryDisabled(): boolean {
      return self.check_history.some(
        (history) => !history.check_date || !history.working_start_date
      );
    },
    get localFilesToUpload(): { formDataName: string; file: IUploadFile }[] {
      const result: { formDataName: string; file: IUploadFile }[] = [];

      const addFile = (formDataName: string, file?: IUploadFile | null) => {
        if (file?.isLocal) {
          result.push({
            formDataName,
            file,
          });
        }
      };

      addFile('attached_passport', self.attached_passport);
      addFile('attached_seal_act', self.attached_seal_act);

      self.check_history.forEach((checkHistoryEl, index: number) => {
        addFile(
          `check_history__${index}__attached_check_act`,
          checkHistoryEl.attached_check_act
        );
        addFile(
          `check_history__${index}__attached_seal_act`,
          checkHistoryEl.attached_seal_act
        );
      });

      return result || [];
    },
  }))
  .actions((self) => ({
    addNewCheckHistory() {
      self.check_history.push({
        attached_check_act: null,
        attached_seal_act: null,
        check_date: null,
        expiration_date_check: 6,
        next_check_date: '',
        working_start_date: '',
      });
    },
    removeCheckHistoryByIndex(historyIndex: number) {
      self.check_history = cast(
        self.check_history.filter((_, index) => index !== historyIndex)
      );
    },
    addFile: flow(function* (
      meterId: string,
      formDataName: string,
      file: File
    ) {
      try {
        const formData = new FormData();
        formData.append(formDataName, file);

        return yield Meters.SendMeterFiles(meterId, formData);
      } catch (error) {
        console.error(error);

        throw error;
      }
    }),
    deleteFile: flow(function* (fileId: string) {
      try {
        return yield Meters.DeleteMeterFile(fileId);
      } catch (error) {
        console.error(error);

        throw error;
      }
    }),
  }));

export const AreaMeterModel = BaseMeterModel.props({
  area: types.optional(AreaModel, {}),
  communication: types.optional(types.string, ''),
  is_automatic: types.optional(types.maybeNull(types.boolean), false),
  mounting: types.optional(types.maybeNull(types.string), null),
  reverse: types.optional(types.boolean, false),
});

export const HouseMeterModel = BaseMeterModel.props({
  house: types.optional(HouseModel, {}),
  adapter: types.optional(types.maybeNull(AdapterModel), null),
  mounting: types.optional(types.maybeNull(types.string), null),
  install_at: types.optional(types.maybeNull(types.string), null),
  started_at: types.optional(types.maybeNull(types.string), null),
  checked_at: types.optional(types.maybeNull(types.string), null),
  temperature_sensor: types.optional(types.maybeNull(types.boolean), null),
  pressure_sensor: types.optional(types.maybeNull(types.boolean), null),
  description_control: types.optional(types.maybeNull(types.string), null),
  serial_number_control: types.optional(types.maybeNull(types.string), null),
  reference: types.optional(types.maybeNull(types.boolean), null),
  meter_model: types.optional(types.maybeNull(types.string), null),
  flowmeter_type: types.optional(types.maybeNull(types.string), null),
  sphere_application: types.optional(types.maybeNull(types.string), null),
  connection_schema: types.optional(
    types.maybeNull(ConnectionSchemaModel),
    null
  ),
  temperature_chart: types.optional(types.maybeNull(types.string), null),
  temperature_circuit: types.optional(types.maybeNull(types.string), null),
  pressure_transformer: types.optional(types.maybeNull(types.string), null),
  winter_calc_formula: types.optional(types.maybeNull(types.string), null),
  summer_calc_formula: types.optional(types.maybeNull(types.string), null),
  code_uute: types.optional(types.maybeNull(types.string), null),
  heat_systems: types.optional(types.array(HeatSystemModel), []),
  season_change: types.optional(types.array(SeasonChangeModel), []),
  equip_info: types.optional(types.array(EquipInfoModel), []),
  address: types.optional(types.maybeNull(types.string), null),
  expances: types.optional(types.maybeNull(ExpancesModel), null),
  tolerance_dt: types.optional(types.number, 3),
  allowable_unbalance_mass: types.optional(types.number, 2),
  state: types.optional(types.maybeNull(types.string), null),
})
  .preProcessSnapshot((snapshot) => {
    return {
      ...snapshot,
      connection_schema: snapshot.connection_schema || {},
      expances: snapshot.expances ?? {},
    };
  })
  .volatile((self) => ({
    initialSnapshot: null as null | typeof self,
    lastGISUploadDate: '',
    isEndReadings: false,
    endReadings: 0,
  }))
  .views((self) => ({
    get isModified(): boolean {
      return (
        !!self.initialSnapshot &&
        !isEqual(getSnapshot(self), self.initialSnapshot)
      );
    },
    getMeterName(meterModelConstants?: IConstant[]): string {
      const name: string[] = [];

      if (self.meter_model) {
        if (meterModelConstants?.length) {
          name.push(getConstantName(meterModelConstants, self.meter_model));
        } else {
          name.push(self.meter_model);
        }
      }

      if (self.serial_number) {
        name.push(`№${self.serial_number}`);
      }

      return !!name.length ? name.join(', ') : 'Неизвестный прибор';
    },
    get defaultHeatHouseMeter(): IHouseMeter {
      return cast({
        adapter: {
          _type: ['DisabledAdapter'],
        },
        _type: ['HeatHouseMeter', 'HouseMeter'],
        unit_of_measurement_okei: '233',
        temperature_sensor: false,
        pressure_sensor: false,
        reference: false,
        digit_capacity: 99999.999,
        house: {},
        initial_values: [0],
        check_history: [
          {
            attached_check_act: null,
            attached_seal_act: null,
            check_date: '',
            expiration_date_check: 6,
            next_check_date: '',
            working_start_date: '',
          },
        ],
      });
    },
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.initialSnapshot = getSnapshot(cast(self));
    },
    setMeter(meter: IHouseMeter) {
      applySnapshot(self, meter);
    },
    setEquipInfo(newValue: IEquipInfo[]) {
      self.equip_info = cast(newValue);
    },
    setConnectionSchema(newValue: IConnectionSchema) {
      self.connection_schema = cast(newValue);
    },
    setSeasonChange(newValue: ISeasonChange[]) {
      self.season_change = cast(newValue);
    },
    setHeatSystems(newValue: IHeatSystem[]) {
      self.heat_systems = cast(newValue);
    },
    setExpances(newValue: IExpances) {
      self.expances = cast(newValue);
    },
    setInitialValues(value: string) {
      self.initial_values = cast([+value]);
    },
    setIsEndReadings(value: boolean) {
      self.isEndReadings = value;
    },
    setEndReadings(value: number) {
      self.endReadings = value;
    },
    setLastGISUploadDate(value: string) {
      self.lastGISUploadDate = value;
    },
  }));

export const MetersStore = types
  .model({
    areaMeters: types.optional(types.array(AreaMeterModel), []),
    houseMeters: types.optional(types.array(HouseMeterModel), []),
    houseMetersByHouseId: types.optional(
      types.map(types.array(HouseMeterModel)),
      {}
    ),
    editableHouseMeter: types.optional(HouseMeterModel, {}),
    replacementHouseMeter: types.optional(HouseMeterModel, {}),
    currentAreaId: types.optional(types.string, ''),
    areaMetersAllReadings: types.optional(
      types.array(AreaMeterAllReadingsModel),
      []
    ),
    checkedMeters: types.optional(
      types.array(types.frozen<INewReadings>()),
      []
    ),
    metersForPatchReadings: types.optional(
      types.array(types.frozen<INewReadings>()),
      []
    ),
    statuses: types.optional(RequestsModel, {}),
  })
  .views((self) => ({
    get valuesSet() {
      const uniqueValues = new Set();

      self.areaMetersAllReadings.forEach((meter) => {
        !!meter.readings?.length &&
          meter.readings[meter.readings.length - 1].values.forEach((value) => {
            uniqueValues.add(
              JSON.stringify({ id: meter.id, value: String(value) })
            );
          });
      });

      return uniqueValues;
    },

    get hasMeterAlert(): boolean {
      const meters = self.areaMetersAllReadings;

      if (!meters.length) {
        return false;
      }

      const now = moment();

      return meters.some((meter) => {
        if (isArchiveMeter(meter.next_check_date, meter.working_finish_date)) {
          return false;
        }

        let lastReadingTooOld = false;

        if (meter.last_readings_date) {
          const lastReadingDate = moment(meter.last_readings_date);
          const daysSinceLastReading = now.diff(lastReadingDate, 'days');
          lastReadingTooOld = daysSinceLastReading > 90;
        }

        let verificationExpiring = false;

        if (meter.next_check_date) {
          const nextCheckDate = moment(meter.next_check_date);
          const daysUntilVerification = nextCheckDate.diff(now, 'days');
          verificationExpiring =
            daysUntilVerification <= 90 && daysUntilVerification >= 0;
        }

        return lastReadingTooOld || verificationExpiring;
      });
    },
  }))
  .views((self) => ({
    get openedMeters(): IAreaMeterAllReadings[] {
      return self.areaMetersAllReadings.filter(
        (meter: IAreaMeterAllReadings) =>
          !isArchiveMeter(meter.next_check_date, meter.working_finish_date)
      );
    },
    get archivedMeters(): IAreaMeterAllReadings[] {
      return self.areaMetersAllReadings.filter((meter: IAreaMeterAllReadings) =>
        isArchiveMeter(meter.next_check_date, meter.working_finish_date)
      );
    },
    getById(id: string) {
      return (
        find(self.areaMetersAllReadings, { id }) || {
          currentReadings: [''],
        }
      );
    },
    get metersFromChecked() {
      const checkedMeters = self.checkedMeters.map((meter) => meter.id);

      return self.areaMetersAllReadings.filter((meter) =>
        checkedMeters.includes(meter.id)
      );
    },
    get hasSomeoneMeterReadingsChanges(): boolean {
      return self.metersForPatchReadings.some((patchMeter) =>
        patchMeter.values.some(
          (patchMeterValue) =>
            !self.valuesSet.has(
              JSON.stringify({ id: patchMeter.id, value: patchMeterValue })
            )
        )
      );
    },
    isMeterReadingsChanges(meter_id: string, index: number): boolean {
      const currentMeter = self.metersForPatchReadings.find(
        (el) => el.id === meter_id
      );

      return (
        !!currentMeter &&
        !self.valuesSet.has(
          JSON.stringify({ id: meter_id, value: currentMeter.values[index] })
        )
      );
    },
    getMetersListByHouseId(
      houseId: string,
      filter: AutomationMetersFilterOptions = 'all',
      isShowArchive: boolean = false
    ): IHouseMeter[] {
      let meters: IHouseMeter[] = self.houseMetersByHouseId.get(houseId) || [];

      if (filter === 'unconfigured') {
        meters = meters.filter(
          (meter: IHouseMeter) => !isConfiguredMeter(meter)
        );
      }

      return isShowArchive
        ? meters.slice().sort((a: IHouseMeter, b: IHouseMeter) => {
            if (a.working_finish_date && !b.working_finish_date) {
              return 1;
            }

            if (!a.working_finish_date && b.working_finish_date) {
              return -1;
            }

            return 0;
          })
        : meters.filter((meter) => !meter.working_finish_date);
    },
    get currentEditableMeterHouse() {
      return self.houseMetersByHouseId.get(
        self.editableHouseMeter.house.id || self.replacementHouseMeter.house.id
      );
    },
  }))
  .actions((self) => ({
    startEditingHouseMeter(meter: IHouseMeter) {
      self.editableHouseMeter = HouseMeterModel.create(clone(meter));
    },
    startReplaceHouseMeter() {
      const houseId = self.editableHouseMeter.house.id;

      self.replacementHouseMeter = detach(self.editableHouseMeter);
      self.editableHouseMeter = cast({
        ...self.editableHouseMeter.defaultHeatHouseMeter,
        house: {
          id: houseId,
        },
      });
    },
    addHouseMeterToHouse(meterId: string) {
      if (self.currentEditableMeterHouse) {
        self.editableHouseMeter.id = meterId;
        self.currentEditableMeterHouse.push(detach(self.editableHouseMeter));
      }

      self.editableHouseMeter = cast({});
    },
    updateHouseMeterInHouse(updatedMeter: IHouseMeter) {
      if (self.currentEditableMeterHouse) {
        const meterIndex = self.currentEditableMeterHouse.findIndex(
          (meter) => meter.id === updatedMeter.id
        );

        if (meterIndex !== -1) {
          self.currentEditableMeterHouse.splice(
            meterIndex,
            1,
            HouseMeterModel.create(clone(updatedMeter))
          );
        }
      }
    },
    deleteHouseMeterFromHouse() {
      if (self.currentEditableMeterHouse) {
        const meterIndexToDelete = self.currentEditableMeterHouse.findIndex(
          (meter) => meter.id === self.editableHouseMeter.id
        );

        if (meterIndexToDelete !== -1) {
          destroy(self.currentEditableMeterHouse[meterIndexToDelete]);
        }
      }

      self.editableHouseMeter = cast({});
    },
  }))
  .actions((self) => ({
    setCheckedMeters(meters: INewReadings[]) {
      self.checkedMeters = cast(meters);
    },
    setMetersForPatchReadings(meters: INewReadings[]) {
      self.metersForPatchReadings = cast(meters);
    },
    clearAreaMeters() {
      self.areaMeters = cast([]);
      self.areaMetersAllReadings = cast([]);
      self.checkedMeters = cast([]);
      self.metersForPatchReadings = cast([]);
      applySnapshot(self.statuses, {});
      self.currentAreaId = '';
    },
    getAreaMeters: flow(function* (params) {
      try {
        self.statuses.areaMeters.fulfilled = false;
        self.statuses.areaMeters.error = false;
        self.statuses.areaMeters.pending = true;
        const response = yield Meters.GetAreaMeters(params);
        self.areaMeters = response.results;
        self.statuses.areaMeters.fulfilled = true;
      } catch (error) {
        console.error(error);
        self.statuses.areaMeters.fulfilled = false;
        self.statuses.areaMeters.error = true;
      } finally {
        self.statuses.areaMeters.pending = false;
      }
    }),
    getAreaMetersAllReadings: flow(function* (params: {
      area: string;
      month_from?: string;
    }) {
      try {
        self.statuses.areaMetersAllReadings.loadingStates();
        const response: any = yield Meters.GetAreaMetersAllReadings(params);
        self.areaMetersAllReadings = response.current_meters;
        self.currentAreaId = params.area;
        self.statuses.areaMetersAllReadings.successStates();

        return response.current_meters;
      } catch (error) {
        console.error(error);
        self.statuses.areaMetersAllReadings.errorStates();
      } finally {
        self.statuses.areaMetersAllReadings.finallyStates();
      }
    }),
    patchAreaMetersReadings: flow(function* (
      areaId: string,
      data: INewAreaMeterReadings[]
    ) {
      try {
        self.statuses.newReadings.loadingStates();
        const response = yield Meters.PatchAreaMeterReadings(areaId, data);
        self.statuses.newReadings.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.newReadings.errorStates();

        throw error;
      } finally {
        self.statuses.newReadings.finallyStates();
      }
    }),
    getHouseMeterById: flow(function* (meterId: string) {
      try {
        self.statuses.meterData.loadingStates();
        const response = yield Meters.GetHouseMetersById(meterId);

        applySnapshot(self.editableHouseMeter, response);
        self.updateHouseMeterInHouse(self.editableHouseMeter);
        self.statuses.meterData.successStates();

        return response;
      } catch (error) {
        self.statuses.meterData.errorStates();
        console.error(error);
      } finally {
        self.statuses.meterData.finallyStates();
      }
    }),
    createHouseMeter: flow(function* () {
      try {
        self.statuses.createMeter.loadingStates();
        const response = yield Meters.CreateHouseMeters(
          killNulls(self.editableHouseMeter.snapshot)
        );

        if (self.editableHouseMeter.localFilesToUpload.length) {
          self.editableHouseMeter.localFilesToUpload.forEach((el) => {
            el.file.upload(() =>
              self.editableHouseMeter.addFile(
                response.id,
                el.formDataName,
                el.file.originalFile!
              )
            );
          });
        }

        self.addHouseMeterToHouse(response.id);
        self.statuses.createMeter.successStates();

        return response;
      } catch (error) {
        self.statuses.createMeter.errorStates();
        console.error(error);
      } finally {
        self.statuses.createMeter.finallyStates();
      }
    }),
    replaceMeter: flow(function* (meterId: string, data: IObject) {
      try {
        self.statuses.replaceMeter.loadingStates();
        const response = yield Meters.ReplaceMeter(meterId, data);

        const currentHouse = self.houseMetersByHouseId.get(
          self.replacementHouseMeter.house.id
        );

        if (!!currentHouse) {
          const replacementMeter = currentHouse.find(
            (meter) => meter.id === meterId
          );
          replacementMeter?.setMeter({
            ...replacementMeter,
            working_finish_date: moment().toISOString(true),
          });
        }

        self.statuses.replaceMeter.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.replaceMeter.errorStates();
      } finally {
        self.statuses.replaceMeter.finallyStates();
      }
    }),
    patchHouseMeter: flow(function* () {
      try {
        self.statuses.patchMeter.loadingStates();

        const response = yield Meters.PatchHouseMeters(
          self.editableHouseMeter.id,
          {
            ...self.editableHouseMeter.snapshot,
            loss_ratio: self.editableHouseMeter.loss_ratio ?? 0,
            working_start_date:
              self.editableHouseMeter.lastCheckHistory.working_start_date,
          }
        );

        self.updateHouseMeterInHouse(self.editableHouseMeter);
        self.statuses.patchMeter.successStates();

        return response;
      } catch (error) {
        self.statuses.patchMeter.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.patchMeter.finallyStates();
      }
    }),
    deleteHouseMeter: flow(function* () {
      try {
        self.statuses.deleteMeter.loadingStates();
        const response = yield Meters.DeleteHouseMeter(
          self.editableHouseMeter.id
        );

        self.deleteHouseMeterFromHouse();
        self.statuses.deleteMeter.successStates();

        return response;
      } catch (error: any) {
        if (error.status === 204) {
          self.deleteHouseMeterFromHouse();
        }

        self.statuses.deleteMeter.errorStates();
        console.error(error);
      } finally {
        self.statuses.deleteMeter.finallyStates();
      }
    }),
    getHeatHouseMeters: flow(function* (params: { house: string }) {
      try {
        self.statuses.heatHouseMeters.loadingStates();
        const response = yield Meters.GetHeatHouseMeters(params);
        self.houseMeters = response.data;
        self.houseMetersByHouseId.set(params.house, response.data);

        self.statuses.heatHouseMeters.successStates();

        return response.data;
      } catch (error) {
        console.error(error);
        self.statuses.heatHouseMeters.errorStates();

        throw error;
      } finally {
        self.statuses.heatHouseMeters.finallyStates();
      }
    }),
    updateHouseMeters: flow(function* (id: string, params: IObject) {
      try {
        self.statuses.heatHouseMeters.loadingStates();
        const response = yield Meters.PatchHouseMeters(id, params);
        self.statuses.heatHouseMeters.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.heatHouseMeters.errorStates();
      } finally {
        self.statuses.heatHouseMeters.finallyStates();
      }
    }),
    sendSettings: flow(function* () {
      try {
        self.statuses.sendSettings.loadingStates();
        const response = yield Meters.SendSettings({
          meter: self.editableHouseMeter.id,
          operator: self.editableHouseMeter.adapter?.gprs_operator,
        });
        self.statuses.sendSettings.successStates();

        return response;
      } catch (error) {
        self.statuses.sendSettings.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.sendSettings.finallyStates();
      }
    }),
  }));
